import { TablePaginationType, UserShortInfo } from '@/shared/types/common';
import {
  ContentMode,
  ILessonFormat,
  ILessonVideo,
  IStudentContentStep,
  LessonTypeEnum,
} from '@/shared/types/lesson';
import type { FileInfo } from '@/shared/types/files';

export type AssignmentAssessmentType = 'summative' | 'formative';
export type AssignmentStatus = 'active' | 'completed';

export interface IAssignmentsRequest {
  page: number;
  pageSize: number;
  assessmentType: AssignmentAssessmentType;
  status: AssignmentStatus;
}

export interface IAssignmentsResponse {
  items: IAssignmentItem[];
  pagination: TablePaginationType;
}

export interface IAssignmentItem {
  course: IAssignmentCourse;
  intake: IAssignmentIntake;
  term: IAssignmentTerm;
  module: IAssignmentModule;
  moduleTeacher: UserShortInfo | null;
  lecturer: UserShortInfo | null;
  lesson: IAssignmentLesson;
  assignment: IAssignmentDetails;
  submission: IAssignmentSubmission | null;
}

export interface IAssignmentCourse {
  id: number;
  title: string;
}

export interface IAssignmentIntake {
  id: number;
  title: string;
}

export interface IAssignmentTerm {
  number: number;
  intakeTermId: number;
}

export interface IAssignmentModule {
  id: number;
  title: string;
  intakeModuleId: number;
}

export interface IAssignmentLesson {
  id: number;
  groupLessonId?: number;
  groupExamResubmissionId?: number;
  assignmentId?: number | null;
  order: number;
  title: string;
  description: string;
  type: LessonTypeEnum;
  format?: ILessonFormat;
  scheduledAt: string;
  endDate?: string | null;
}

export interface IAssignmentContextRequest {
  groupLessonId?: number;
  groupExamResubmissionId?: number;
}

export interface IAssignmentContextCourse {
  id: number;
  title: string;
  type: string;
}

export interface IAssignmentContextIntake {
  id: number;
  title: string;
}

export interface IAssignmentContextTerm {
  number: number;
  intakeTermId: number;
  startDate: string;
  endDate: string;
}

export interface IAssignmentContextGroup {
  id: number;
  number: number;
}

export interface IAssignmentContextModule {
  id: number;
  intakeModuleId: number;
  title: string;
}

export interface IAssignmentContextLesson {
  id: number;
  groupLessonId?: number;
  groupExamResubmissionId?: number;
  assignmentId?: number | null;
  title: string;
  description: string | null;
  order: number;
  type: LessonTypeEnum;
  format?: ILessonFormat;
  scheduledAt: string;
  endDate: string;
  duration?: number | null;
  video: ILessonVideo | null;
  contentMode: ContentMode;
  steps: IStudentContentStep[];
}

export interface IAssignmentContextAssignment {
  id: number;
  text: string;
  description: string;
  attempts: number;
  attachments: FileInfo[];
}

export interface IAssignmentNextLesson {
  id: number;
  groupLessonId?: number;
  groupExamResubmissionId?: number;
  order: number;
  title: string;
}

export interface IAssignmentContextResponse {
  course: IAssignmentContextCourse;
  intake: IAssignmentContextIntake;
  term: IAssignmentContextTerm;
  group: IAssignmentContextGroup;
  module: IAssignmentContextModule;
  lesson: IAssignmentContextLesson;
  nextLesson: IAssignmentNextLesson | null;
  assignment: IAssignmentContextAssignment | null;
  moduleTeacher: UserShortInfo | null;
  lecturer: UserShortInfo | null;
  totalStudents?: number;
  totalCompleted?: number;
}

export interface IAssignmentSubmissionQueryRequest {
  groupLessonId?: number;
  groupExamResubmissionId?: number;
}

export interface IAssignmentSubmissionCommentAuthor {
  id: number;
  username: string;
  avatar: string | null;
  role: string;
}

export interface IAssignmentSubmissionComment {
  id: number;
  text: string;
  createdAt: string;
  attachments: FileInfo[];
  author: IAssignmentSubmissionCommentAuthor;
}

export interface IAssignmentSubmissionDetails {
  id: number;
  text: string;
  assignmentStatus: AssignmentSubmissionStatusEnum;
  grade: number | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedBy: IAssignmentSubmissionCommentAuthor | null;
  reviewCount: number;
  submissionComments: IAssignmentSubmissionComment[];
  files: FileInfo[];
  turnitin: IAssignmentTurnitinData | null;
}

export interface IAssignmentSubmissionStudentInfo {
  id: number;
  enrollmentId: number;
  username: string;
  avatar: string | null;
}

export interface IAssignmentSubmissionQueryResponse {
  student: IAssignmentSubmissionStudentInfo;
  submission: IAssignmentSubmissionDetails;
}

export enum AssignmentSubmissionStatusEnum {
  AwaitingReview = 'awaiting_review',
  Completed = 'completed',
  Failed = 'failed',
}

export const ASSIGNMENT_SUBMISSION_STATUS_LABELS: Record<AssignmentSubmissionStatusEnum, string> = {
  [AssignmentSubmissionStatusEnum.AwaitingReview]: 'Awaiting Review',
  [AssignmentSubmissionStatusEnum.Completed]: 'Pass',
  [AssignmentSubmissionStatusEnum.Failed]: 'Failed',
};

export interface IAssignmentDetails {
  id: number;
  title: string;
  description: string;
  attachments: FileInfo[];
  attempts: number;
}

export interface IAssignmentSubmission {
  id: number;
  grade: number | null;
  assignmentStatus: AssignmentSubmissionStatusEnum;
  reviewCount: number;
  submittedAt: string | null;
  turnitinData: IAssignmentTurnitinData | null;
}

export interface IAssignmentSubmissionRequest {
  text: string;
  files?: File[];
  groupLessonId?: number;
  groupExamResubmissionId?: number;
}

export interface IAssignmentSubmissionResponse {
  id: number;
}

export interface IAssignmentTurnitinData {
  status: string;
  similarityStatus: string | null;
  matchesPercentage: number | null;
  internetPercentage: number | null;
  publicationPercentage: number | null;
  submittedWorksPercentage: number | null;
  statusErrorMessage: string | null;
  statusSimilarityErrorMessage: string | null;
}

export interface IAssignmentNormalizedItem {
  module: { id: number; title: string };
  lesson: {
    id: number;
    groupLessonId?: number;
    groupExamResubmissionId?: number;
    title: string;
    order: number;
    createdAt: string;
    deadline: string;
    type: LessonTypeEnum;
  };
  assignment: {
    id: number;
    title: string;
    description: string;
    attempts: number;
    attachments?: FileInfo[];
  };
  submissionId: number | null;
  grade: number | null;
  assignmentStatus: AssignmentSubmissionStatusEnum | null;
  submissionSubmittedAt: string | null;
  retake: number;
  turnitinData: IAssignmentTurnitinData | null;
  moduleTeacher: UserShortInfo | null;
  lecturer: UserShortInfo | null;
  buttonData?: { text: string; href: string };
}
